import React, {useEffect, useState, useCallback, useRef} from "react";
import {
    BoxIsolatedContent,
    ContentTop,
    ContentXsTop,
    FlexColumn,
    FlexColumnCentered,
    FlexRow,
    FormFooter,
    FormGoBack,
    IconContainer,
} from "../../../../components/ui/Content";
import {Body1, Body1Pointer, Body1PointerLink, Body2, Caption2, Title} from "../../../../components/Font";
import {ButtonPrimary} from "../../../../components/ui/Button";
import {useTheme} from "styled-components";
import {useTranslation} from "react-i18next";
import {InputAutoCompleteEnum, InputElement, InputTypeEnum} from "../../../../ui/Input/Input";
import {
    Authorization,
    BindConnection,
    CheckConnections,
    CheckConnectionsAdmin,
    cleanError,
    ContinueConnection, forceSetToUserChoose,
    hidePasswordExpiryWarning,
    resetConnectionState,
    clearAndExit,
    clearSecondFactor,
    setAccountToConnectId,
    setIsTryingAsAdmin,
    SignIn,
    SkipConnection,
    setIsEmailPFUR,
    addError,
} from "../../slice";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {LoaderContainer, LoaderEnum, MiniLoader} from "../../../../ui/Loader/MiniLoader";
import {ForgotPassword, LoginInputContainer, RegisterInputContainer} from "../../../../components/ui/InputSearch";
import {CircleQuestion, GoBack, GreenCircleCheck, GreenConnection} from "../../../../icon/Icon";
import {LanguageSelector} from "../../LanguageSelector";
import {useWindowResize} from "../../../../hooks/useWindowSize";
import styled from "styled-components/macro";
import {useLocation, useNavigate} from "react-router-dom";
import {RecoveryAccount} from "../../RecoverySteps/RecoveryAccount";
import {trackHit} from "../../../../utils/analytics";
import {PasswordExpiryWarning} from "../PasswordExpiryWarning";
import { TwoFactorConfirmation } from "./TwoFactorConfirmation";
import { ConfirmationWayEnum } from "../../../../enum/confirmationWayEnum";

export const LogInForm = () => {

    const Theme = useTheme()
    const {t} = useTranslation();
    const [hint, setHint] = useState({
        show: false,
        coords: {
            top: 0,
            left: 0
        }
    })
    const [user, setUser] = useState({
        login: '',
        password: ''
    })

    const [connectPassword, setConnectPassword] = useState('')
    const [confirmed, setConfirmed] = useState(false)
    const [connectWarningShow, setConnectWarningShow] = useState(false)

    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const {search} = useLocation()
    const {
        error,
        isAuthFetching,
        connection,
        isFetching,
        token,
        needAuth,
        isTryingAsAdmin,
        unbindingsAccounts,
        secondFactorTotalTtlMinutes,
        passwordExpiryWarning,
        isEmailPFUR,
    } = useAppSelector(state => state.auth)

    React.useEffect(() => {
        trackHit();
    }, [search]);

    const {
        isBinding,
        token: connectToken,
        data: connections,
        accountToConnectId,
        connectedAccountId,
        accountSubmitted,
        canLogIn,
    } = connection

    const connectedAccount = connections.find(i => i.common_name === connectedAccountId)
    const accountToConnect = connections.find(i => i.common_name === accountToConnectId)

    const {isDesktop} = useWindowResize()

    const waitSecondFactor = useAppSelector(state => state.auth.waitSecondFactor)

    useEffect(() => {
        const input = document.querySelector('.loginNameInput') as HTMLInputElement
        input?.focus()
    }, [])

    useEffect(() => {
        setConnectPassword('')
    }, [confirmed])

    useEffect(() => {
        const wait2FA = typeof waitSecondFactor === 'boolean' ? waitSecondFactor : false
        if (connectToken && canLogIn && !token && !wait2FA && !passwordExpiryWarning.show) {
            if (isTryingAsAdmin) {
                submitAdmin()
            } else {
                submitAuth(connectToken)
            }
        }
    }, [connectToken, canLogIn, token, isTryingAsAdmin, waitSecondFactor, passwordExpiryWarning.show])

    useEffect(() => {
        // Не выполняем привязку аккаунта, если показано предупреждение о пароле
        if (needAuth === false && !passwordExpiryWarning.show && accountToConnect && connectToken) {
            bindConnection()
        }
    }, [needAuth, passwordExpiryWarning.show, accountToConnect, connectToken])

    const [twoFactorError, setTwoFactorError] = useState<string | null>(null)

    const extractErrorMessage = useCallback((error: unknown): string => {
        if (!error) {
            return t('auth.twoFactorError', 'Не удалось инициировать двухфакторную авторизацию')
        }
        if (typeof error === 'string') {
            return error
        }
        if (typeof error === 'object') {
            const err = error as { message?: string; description?: string; response?: { data?: { error?: { message?: string; description?: string } } } }
            return err?.message
                || err?.description
                || err?.response?.data?.error?.message
                || err?.response?.data?.error?.description
                || t('auth.twoFactorError', 'Не удалось инициировать двухфакторную авторизацию')
        }
        return t('auth.twoFactorError', 'Не удалось инициировать двухфакторную авторизацию')
    }, [t])

    const adminIntentRef = useRef(false)

    const submit = (adminIntent: boolean) => {
        adminIntentRef.current = adminIntent
        if (adminIntent) {
            dispatch(CheckConnectionsAdmin({
                username: user.login,
                password: user.password
            }))
            .unwrap()
            .then(() => {
                setTwoFactorError(null)
                localStorage.removeItem('selectedUser')
            })
            .catch((error) => {
                setTwoFactorError(extractErrorMessage(error))
                dispatch(clearAndExit())
            })
        } else {
            dispatch(CheckConnections({
                username: user.login,
                password: user.password,
                ad_person_id: null
            }))
            .unwrap()
            .then(() => {
                setTwoFactorError(null)
                localStorage.removeItem('selectedUser')
            })
            .catch((error) => {
                setTwoFactorError(extractErrorMessage(error))
                dispatch(clearAndExit())
            })
        }
        localStorage.setItem('userLogin', user.login)
    }

    const handleSecondFactorSuccess = useCallback(() => {
        dispatch(clearSecondFactor())
        setTwoFactorError(null)
        if (connection.token) {
            submitAuth(connection.token)
        }
    }, [dispatch, connection.token])

    const handleAdminSecondFactorSuccess = useCallback(() => {
        dispatch(clearSecondFactor())
        setTwoFactorError(null)
        localStorage.removeItem('selectedUser')
        dispatch(CheckConnectionsAdmin({
            username: user.login,
            password: user.password
        }))
        .unwrap()
        .catch((error) => {
            setTwoFactorError(extractErrorMessage(error))
        })
    }, [dispatch, user.login, user.password, extractErrorMessage])

    // 2FA: локальный ввод кода
    const secondFactor = useAppSelector(state => state.auth.secondFactor)

    const [show2FA, setShow2FA] = useState(false)
    useEffect(() => {
        if (secondFactor?.operationId && connection.token) {
            setShow2FA(true)
        } else {
            setShow2FA(false)
        }
    }, [secondFactor?.operationId, connection.token])

    // При старте 2FA очищаем выбранного пользователя, чтобы не триггерился повторный CheckConnections
    useEffect(() => {
        if (secondFactor?.operationId && connection.token) {
            localStorage.removeItem('selectedUser')
        }
    }, [secondFactor?.operationId, connection.token])

const [selectedUser, setSelectedUser] = useState(localStorage.getItem('selectedUser'))
useEffect(() => {
    if (!selectedUser) {
        return
    }
    // Блокируем автозапуск обычного потока, если начинается админ-флоу или уже идёт 2FA
    if (isTryingAsAdmin || adminIntentRef.current || secondFactor?.operationId) {
        return
    }
    dispatch(clearSecondFactor())
    setTwoFactorError(null)
    dispatch(CheckConnections({
        username: user.login,
        password: user.password,
        ad_person_id: Number(selectedUser)
    }))
}, [selectedUser, isTryingAsAdmin, user.login, user.password, dispatch, secondFactor?.operationId])
    const bindConnection = () => {
        if (accountToConnect && connectToken) {
            dispatch(BindConnection({
                data: {
                    common_name: accountToConnect.common_name,
                    password: connectPassword || null,
                    ad_id: accountToConnect.ad_id
                },
                token: connectToken
            })).then(() => {
                setConnectWarningShow(false)
            })
        }
    }

    const submitAuth = (connectToken: string) => {
        const urlSearch = new URLSearchParams(window.location.search)
        const client_id = urlSearch.get('client_id')
        if (client_id) {
            dispatch(Authorization(connectToken))
        } else {
            dispatch(SignIn(connectToken))
        }
    }

    const submitAdmin = () => {
        if (isTryingAsAdmin) {
            dispatch(CheckConnectionsAdmin({
                username: user.login,
                password: user.password
            }))
                .unwrap()
                .then(() => {
                    setTwoFactorError(null)
                    localStorage.removeItem('selectedUser')
                })
                .catch((error) => {
                    setTwoFactorError(extractErrorMessage(error))
                    dispatch(clearAndExit())
                })
        } else {
            dispatch(forceSetToUserChoose())
        }
    }

    const onSubmitButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        const adminIntent = e.ctrlKey || e.metaKey
        dispatch(setIsTryingAsAdmin(adminIntent))
        submit(adminIntent)
    }

    const [longPress, setLongPress] = useState(false);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

    const onTouchStart = () => {
        const id = setTimeout(() => {
            dispatch(setIsTryingAsAdmin(true));
            setLongPress(true);
            submit(true)
        }, 5000); // мс
        setTimeoutId(id);
    };
    const onTouchEnd = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        if (!longPress) {
            dispatch(setIsTryingAsAdmin(false));
            submit(false)
        }
        setLongPress(false);
    }

    if (secondFactor?.operationId && connection.token) {
        return (
            <>
                <FlexColumnCentered className={'box-isolated'}>
                    <Title>{t('auth.enterTwoFactorCode', 'Подтвердите вход')}</Title>
                    <BoxIsolatedContent>
                        <ContentXsTop>
                            <Body1 color={Theme.color.secondary}>
                                {t('auth.waitSecondFactorMessage', 'На ваш номер отправлен код подтверждения. Введите его, чтобы продолжить.')}
                            </Body1>
                        </ContentXsTop>
                    </BoxIsolatedContent>
                    <LanguageSelector/>
                </FlexColumnCentered>
                <TwoFactorConfirmation
                    operationId={secondFactor.operationId}
                    token={connection.token}
                    isAdmin={isTryingAsAdmin}
                    totalTTLMinutes={secondFactorTotalTtlMinutes ?? undefined}
                    onClose={() => {
                        dispatch(clearSecondFactor())
                        dispatch(clearAndExit())
                    }}
                    onSuccess={handleSecondFactorSuccess}
                    onAdminSuccess={handleAdminSecondFactorSuccess}
                    onError={(message) => setTwoFactorError(message)}
                />
                {twoFactorError && (
                    <Body1 style={{ color: Theme.color.danger, marginTop: 16, textAlign: 'center' }}>
                        {twoFactorError}
                    </Body1>
                )}
            </>
        )
    }

    if (connections.length > 0) {

        if (accountSubmitted) {

            return <><FlexColumnCentered className={'box-isolated'}>
                <Title>{t('auth.accountsBinding')}</Title>
                <BoxIsolatedContent>
                    <ContentXsTop>
                        <Body1 color={Theme.color.secondary}>
                            {`Мы связали аккаунт ${connectedAccount?.common_name} (${connectedAccount?.email}) с данной учетной записью`}
                        </Body1>
                    </ContentXsTop>

                    {isBinding ? <ButtonPrimary>
                            <LoaderContainer>
                                <MiniLoader color={Theme.color.primaryBackground} size={LoaderEnum.small}/>
                            </LoaderContainer>
                        </ButtonPrimary>
                        : <ButtonPrimary onClick={() => {
                            if (connectToken) {
                                dispatch(ContinueConnection(connectToken)).then(() => {
                                    submitAuth(connectToken)
                                })
                            }
                        }
                        }>
                            {t('auth.logIn')}
                        </ButtonPrimary>}
                </BoxIsolatedContent>
                <LanguageSelector/>
            </FlexColumnCentered>
            </>
        }

        if (confirmed) {

            if (connectWarningShow) {
                return <><FlexColumnCentered className={'box-isolated'}>
                    <Title>{t('auth.attention')}!</Title>
                    <BoxIsolatedContent>
                        <ContentXsTop>
                            <Body1 color={Theme.color.secondary}>
                                {t('auth.importedAccountsMessage')}
                            </Body1>
                        </ContentXsTop>

                        {isBinding ? <ButtonPrimary>
                                <LoaderContainer>
                                    <MiniLoader color={Theme.color.primaryBackground} size={LoaderEnum.small}/>
                                </LoaderContainer>
                            </ButtonPrimary>
                            :
                            <ButtonPrimary disabled={needAuth !== false && connectPassword.length < 3} onClick={() => {
                                if (connectPassword.length > 3) {
                                    bindConnection()
                                } else {
                                    if (needAuth === false) {
                                        bindConnection()
                                    }
                                }
                            }
                            }>
                                {t('auth.bind')}
                            </ButtonPrimary>}
                    </BoxIsolatedContent>
                    <ContentTop>
                        <Body1PointerLink onClick={() => {
                            setConnectWarningShow(false)
                        }}>
                            {t('auth.cancel')}
                        </Body1PointerLink>
                    </ContentTop>
                    <LanguageSelector/>
                </FlexColumnCentered>
                </>
            }

            return <><FlexColumnCentered className={'box-isolated'}>
                <FormGoBack style={{left: 24, top: 34}} onClick={() => {
                    if (error['password']) {
                        dispatch(cleanError('password'))
                    }
                    setConfirmed(false)
                }}>
                    <GoBack/>
                </FormGoBack>
                <Title>{t('auth.accountBind')}</Title>
                <BoxIsolatedContent>
                    <ContentXsTop>
                        <Body1 color={Theme.color.secondary}>
                            {t('auth.confirmOperationEnterPassword')}
                        </Body1>
                    </ContentXsTop>
                    <ContentXsTop style={{width: '100%', textAlign: 'start', marginTop: 30}}>
                        <Caption2 color={Theme.color.secondary}>
                            {t('auth.logIn')}
                        </Caption2>
                        <Body1>
                            {accountToConnect?.common_name}
                        </Body1>
                    </ContentXsTop>
                    <ContentXsTop style={{width: '100%', textAlign: 'start'}}>
                        <Caption2 color={Theme.color.secondary}>
                            {t('auth.emailEnter')}
                        </Caption2>
                        <Body1>
                            {accountToConnect?.email}
                        </Body1>
                    </ContentXsTop>

                    {needAuth !== false && <RegisterInputContainer>
                        <InputElement
                            type={InputTypeEnum.Password}
                            error={error['password']}
                            placeholder={t('auth.passwordPlaceholder')}
                            value={connectPassword}
                            onChange={(value) => {
                                if (error['password']) {
                                    dispatch(cleanError('password'))
                                }
                                setConnectPassword(value)
                            }}
                            onKeyDown={(e) => {
                                if (error['password']) {
                                    dispatch(cleanError('password'))
                                }
                                if (e === 'Enter' && (connectPassword.length > 3)) {
                                    setConnectWarningShow(true)
                                }
                            }}
                            autoComplete={InputAutoCompleteEnum.NewPassword}
                        />
                    </RegisterInputContainer>}


                    <ButtonPrimary disabled={needAuth !== false && connectPassword.length < 4} onClick={() => {
                        setConnectWarningShow(true)
                    }}>
                        {t('auth.bind')}
                    </ButtonPrimary>
                </BoxIsolatedContent>
                <LanguageSelector/>
            </FlexColumnCentered>
            </>
        }

        return <><FlexColumnCentered className={'box-isolated'}>
            <Title>{t('auth.accountBind')}</Title>
            <BoxIsolatedContent>
                <ContentXsTop>
                    <Body1 color={Theme.color.secondary}>
                        {t('auth.selectPrimaryDomain')}
                    </Body1>
                </ContentXsTop>
                <ContentXsTop/>

                {connections.map((item, index) => {
                    return <FlexRow
                        className={'svg-min16'}
                        style={{
                            cursor: 'pointer',
                            width: '100%',
                            padding: '12px 0',
                            alignItems: 'center',
                            gap: '12px',
                            borderBottom: index !== (connections.length - 1)
                                ? '0.5px solid rgba(0, 0, 0, 0.12)'
                                : ''
                        }}
                        onClick={() => {
                            if (accountToConnectId === item.common_name) {
                                dispatch(setAccountToConnectId(null))
                            } else {
                                dispatch(setAccountToConnectId(item.common_name))
                            }
                        }} key={index}>
                        {accountToConnectId === item.common_name && <IconContainer>
                            <GreenCircleCheck/>
                        </IconContainer>}
                        <FlexColumn style={{
                            alignItems: 'start',
                            gap: '2px',
                            width: '100%',

                        }}>
                            <Body2>
                                {item.common_name}
                            </Body2>
                            <Caption2>
                                {item.email}
                            </Caption2>
                        </FlexColumn>
                        {connectedAccount?.common_name === item.common_name && <GreenConnection/>}
                    </FlexRow>
                })}

                {!isBinding && <ButtonPrimary disabled={!accountToConnect} onClick={() => {
                    if (accountToConnect) {
                        setConfirmed(true)
                    }
                }}>
                    {t('auth.bind')}
                </ButtonPrimary>}
            </BoxIsolatedContent>
            <ContentTop>
                <Body1PointerLink onClick={() => {
                    if (connectToken && !isBinding) {

                        dispatch(SkipConnection(connectToken)).then(() => {
                            dispatch(ContinueConnection(connectToken)).then(() => {
                                submitAuth(connectToken)
                            })
                        })
                    }
                }}>
                    {(isBinding || isFetching)
                        ? <MiniLoader color={Theme.color.indicator} size={LoaderEnum.small}/>
                        : t('auth.skipAndGoToProfile')}
                </Body1PointerLink>
            </ContentTop>
            <LanguageSelector/>
        </FlexColumnCentered>
        </>
    }

    // Обработчики для модального окна предупреждения о пароле
    const handleSkipPasswordWarning = () => {
        dispatch(hidePasswordExpiryWarning())
        // После скрытия модалки продолжаем нормальный флоу авторизации
        // useEffect-ы автоматически запустят нужный флоу, так как passwordExpiryWarning.show станет false
    }

    const handleChangePassword = () => {
        dispatch(hidePasswordExpiryWarning())
        dispatch(resetConnectionState())
        // Очищаем localStorage от временных данных
        localStorage.removeItem('selectedUser')
        navigate(`/password/recovery${search}`)
    }

    // Показываем модальное окно предупреждения о пароле ПЕРЕД проверкой несвязанных аккаунтов
    if (passwordExpiryWarning.show && passwordExpiryWarning.daysLeft !== null) {
        return <PasswordExpiryWarning
            daysLeft={passwordExpiryWarning.daysLeft}
            onSkip={handleSkipPasswordWarning}
            onChangePassword={handleChangePassword}
        />
    }

    if(unbindingsAccounts && !selectedUser){
        return <RecoveryAccount setSelectedUserAuth={setSelectedUser} />
    }

    return <><FlexColumnCentered className={'box-isolated'}>
        <BoxIsolatedContent>

            <Title>{t('auth.logInAccount')}</Title>

            <LoginInputContainer>
                <InputElement
                    className={'loginNameInput'}
                    error={error['username']}
                    placeholder={t('auth.loginTitle')}
                    value={user.login}
                    onChange={(value) => {
                        if (error['username']) {
                            dispatch(cleanError('username'))
                        }
                        if (value.includes('@pfur.ru')) {
                            dispatch(setIsEmailPFUR(true))
                            let emailRUDN = ''
                            try {
                                emailRUDN = value.split('@')[0].replace('_', '-') + '@rudn.ru'
                            } catch (error) {
                                emailRUDN = 'ivanov-ii@rudn.ru'
                            }
                            dispatch(addError({key: 'username', value: t('auth.emailPFURError', {email: emailRUDN})}))
                        } else {
                            dispatch(setIsEmailPFUR(false))
                            dispatch(cleanError('username'))
                        }
                        setUser({...user, login: value})
                    }}
                    disabled={isAuthFetching}
                />
                {isDesktop && <IconContainer
                    onMouseEnter={(e) => {
                        setHint({
                            show: true,
                            coords: {
                                top: e.currentTarget.getBoundingClientRect().top,
                                left: e.currentTarget.getBoundingClientRect().left
                            }
                        })
                    }}
                    onMouseLeave={() => {
                        setHint({
                            ...hint,
                            show: false
                        })
                    }}
                    style={{
                        position: 'absolute',
                        right: '15px',
                        top: '40px'
                    }}>
                    <CircleQuestion/>
                </IconContainer>}

            </LoginInputContainer>
            <Hint
                className={hint.show ? 'visibleHint' : 'invisibleDelay'}
                style={{
                    top: (hint.coords.top - 80) + 'px',
                    left: (hint.coords.left + 40) + 'px'
                }}>
                <Body1 style={{textAlign: 'start'}}>
                    {t('auth.loginCanBe')}
                </Body1>
                <ul>
                    <li>{t('auth.corporateMail')}</li>
                    <li>{t('auth.electronicMail')}</li>
                    <li>{t('auth.mobilePhone')}</li>
                </ul>
            </Hint>

            {!isDesktop && <Body1 style={{textAlign: 'start', margin: '12px 0 0'}}>
                {t('auth.loginHint')}
            </Body1>}

            <RegisterInputContainer>
                <InputElement
                    type={InputTypeEnum.Password}
                    error={error['password']}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={user.password}
                    onChange={(value) => {
                        if (error['password']) {
                            dispatch(cleanError('password'))
                        }
                        setUser({...user, password: value})
                    }}
                    onKeyDown={(e) => {
                        if (error['password']) {
                            dispatch(cleanError('password'))
                        }
                        if (e === 'Enter' && (user.login.length > 1 && user.password.length > 1)) {
                            submit(isTryingAsAdmin)
                        }
                    }}
                    disabled={isAuthFetching}
                    autoComplete={InputAutoCompleteEnum.CurrentPassword}
                />
            </RegisterInputContainer>

            <ForgotPassword>
                <Body1Pointer color={Theme.color.indicator} onClick={() => {
                    navigate(`/password/recovery${search}`)
                }}>
                    {t('auth.forgotPassword')}
                </Body1Pointer>
            </ForgotPassword>

            {(isAuthFetching || isFetching || waitSecondFactor)
                ? <ButtonPrimary>
                    <LoaderContainer>
                        <MiniLoader color={Theme.color.primaryBackground} size={LoaderEnum.small}/>
                    </LoaderContainer>
                </ButtonPrimary>
                : <ButtonPrimary disabled={waitSecondFactor || user.login.length < 1 || user.password.length < 1 || isEmailPFUR}
                    onClick={onSubmitButtonClick}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {t('auth.logIn')}
                </ButtonPrimary>}
        </BoxIsolatedContent>
        <FormFooter style={{borderTop: Theme.border.default}}>
            <Body1PointerLink onClick={() => {
                navigate(`/sign-up${search}`)
            }}>
                {t('auth.createAccount')}
            </Body1PointerLink>
        </FormFooter>
        <LanguageSelector/>
        {show2FA && secondFactor?.operationId && connection.token && (
            <TwoFactorConfirmation
                operationId={secondFactor.operationId}
                token={connection.token}
                isAdmin={isTryingAsAdmin}
                defaultWay={secondFactor.wayId === ConfirmationWayEnum.CallBack ? 'callback' : 'sms'}
                totalTTLMinutes={secondFactorTotalTtlMinutes ?? undefined}
                onClose={() => {
                    setShow2FA(false);
                    dispatch(clearSecondFactor());
                    setTwoFactorError(null);
                }}
                onSuccess={handleSecondFactorSuccess}
                onAdminSuccess={handleAdminSecondFactorSuccess}
                onError={(msg) => {
                    setTwoFactorError(msg);
                }}
            />
        )}
{(twoFactorError) && (
    <Body1 style={{ color: Theme.color.danger }}>
        {twoFactorError}
    </Body1>
)}
    </FlexColumnCentered>
    </>
}

const Hint = styled.div`
  display: flex;
  position: fixed;
  padding: 24px;
  flex-direction: column;
  gap: 8px;
  border-radius: 9px;
  background: #FFFFFF;
  width: 214px;
  filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.08)) drop-shadow(0px 2px 24px rgba(0, 0, 0, 0.08));
  z-index: 100;

  ul {
    margin: 0;
    padding-left: 15px;

    li {
      text-align: start;
    }
  }

  :after {
    background: #FFFFFF;
    display: flex;
    content: '';
    width: 20px;
    height: 20px;
    border-radius: 4px;
    position: absolute;
    top: 50%;
    left: -15px;
    transform: rotate(45deg) translateY(-50%);
  }
`
